/**
 * Presentation helpers for the driver portal.
 *
 * Times are pinned to Europe/Budapest via the app's existing timezone plumbing
 * (`src/Utils/timezone.js`) rather than the device clock: a driver's phone can
 * sit on a foreign carrier's timezone and the pickup time must not move.
 *
 * Day and month names come from dayjs's shipped Hungarian locale, applied per
 * instance (`.locale('hu')`) so the admin app's global locale is untouched.
 */
import dayjs from 'dayjs';
import 'dayjs/locale/hu';
import {
  DEFAULT_TIMEZONE, formatDate, formatTime, utcToLocal,
} from '../../Utils/timezone';
import {
  isRunningTimer, overtimeEntries, overtimeTotalMinutes,
} from '../../Utils/driverOvertime';
import { PUSH_SUPPORT } from '../../Services/PushNotifications';
import { PORTAL_USER_KINDS } from '../../config/config';

const inHungarian = (value) => (value ? utcToLocal(value).locale('hu') : null);

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * `{ from, to }` as the YYYY-MM-DD Budapest days the trips endpoint filters
 * on: `back` days before today through `ahead` days after it.
 */
export const budapestDayWindow = (back, ahead, now = new Date()) => {
  const today = utcToLocal(now);
  return {
    from: formatDate(today.subtract(back, 'day')),
    to: formatDate(today.add(ahead, 'day')),
  };
};

/* --- calendar days ------------------------------------------------------ */
// A driver's day is a Budapest calendar date, 'YYYY-MM-DD' — the shape the
// trips endpoint filters on. Arithmetic on it runs in UTC, where every day is
// 24 hours, so a DST change can never shift or repeat a date.

const calendar = (day) => dayjs.utc(day).locale('hu');

/** True for a real 'YYYY-MM-DD' date (not '2026-02-30'). */
export const isCalendarDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
  && dayjs.utc(value).format('YYYY-MM-DD') === value;

export const addDays = (day, count) => calendar(day).add(count, 'day').format('YYYY-MM-DD');

/** The Monday-to-Sunday week `day` falls in, as seven calendar days. */
export const weekOf = (day) => {
  const monday = addDays(day, -((calendar(day).day() + 6) % 7));
  return [0, 1, 2, 3, 4, 5, 6].map((offset) => addDays(monday, offset));
};

// The standard Hungarian short weekday names — H, K, Sze, Cs, P, Szo, V —
// from the platform's own locale data rather than a hand-kept list.
const WEEKDAY_SHORT = new Intl.DateTimeFormat('hu-HU', {
  weekday: 'short', timeZone: DEFAULT_TIMEZONE,
});

/** 'Cs' — the week strip's column head. Noon UTC is that same date in Budapest. */
export const weekdayShort = (day) => WEEKDAY_SHORT.format(new Date(`${day}T12:00:00Z`));

/** 18 */
export const dayOfMonth = (day) => calendar(day).date();

/** 'szeptember 2026' — above the week strip. */
export const monthLabel = (day) => calendar(day).format('MMMM YYYY');

/** 'szeptember 18., csütörtök, 3 út' — what a screen reader hears for a day. */
export const dayAriaLabel = (day, trips) => `${calendar(day).format('MMMM D., dddd')}, ${
  trips ? `${trips} út` : 'nincs út'}`;

/** 'Szerda · szeptember 16.' */
export const longDate = (value) => {
  const at = inHungarian(value);
  return at ? capitalise(at.format('dddd · MMMM D.')) : '';
};

/** 'Ma' / 'Holnap' / 'Tegnap' / 'szeptember 18.' — relative to the given "now". */
export const dayLabel = (value, now = new Date()) => {
  const at = inHungarian(value);
  if (!at) return '';
  const diff = at.startOf('day').diff(utcToLocal(now).startOf('day'), 'day');
  if (diff === 0) return 'Ma';
  if (diff === 1) return 'Holnap';
  if (diff === -1) return 'Tegnap';
  return at.format('MMMM D.');
};

/**
 * 'Most' / '35 perc múlva' / '2 óra múlva'. Returns '' once a trip is far
 * enough in the past that a countdown would be noise.
 */
export const countdown = (value, now = new Date()) => {
  if (!value) return '';
  const minutes = Math.round((new Date(value).valueOf() - new Date(now).valueOf()) / 60000);
  if (minutes <= 5 && minutes >= -15) return 'Most';
  if (minutes < -15) return '';
  if (minutes < 60) return `${minutes} perc múlva`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} óra múlva`;
  return '';
};

/** 'Heather Sanford' -> 'HS'. Falls back to one letter, then to ''. */
export const initials = (name) => String(name || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0])
  .join('')
  .toUpperCase();

export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/* --- typing at a list, on a phone ---------------------------------------
 * ONE normaliser for every filter-as-you-type box in the portal: the
 * dispatch board's search over trips (dispatcherFormat's `matchesSearch`)
 * and the assign sheet's operator / driver / vehicle pickers
 * (Components/ChoicePicker). A second one would drift, and the first thing
 * to drift would be the accents. The dashboard's Month End counterparty
 * filter reuses it word by word (`matchesEveryWord` in Pages/MonthEnd) for
 * the same reason: "toth" has to find "Tóth" there too.
 */

/**
 * Text reduced to what somebody types with their thumb: case is ignored, and
 * so is anything that is not a letter or a digit — so `ab123` finds plate
 * `AB-123`, `pu_dash_aaa` finds `PU_DASH_AAA`, and a name typed without its
 * accents still finds it (`NFD` splits the mark off the letter, and the
 * range below drops it).
 */
export const looseText = (text) => String(text || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

/** Does `text` answer what was typed? An empty query matches everything. */
export const looseIncludes = (text, query) => {
  const needle = looseText(query);

  return needle ? looseText(text).includes(needle) : true;
};

/** "Mercedes E-Class · NLR-482" — a vehicle as a person reads it, or ''. */
export const vehicleName = (vehicle) => [vehicle?.type, vehicle?.plate_number]
  .filter(Boolean)
  .join(' · ');

/**
 * The trip's resolved `vehicle` by name. Never falls back to `car_id` — that
 * is an opaque id, not something a driver can read.
 */
export const vehicleLabel = (trip) => vehicleName(trip?.vehicle);

/** The API's session view names a driver login `driver_name`, an operator one `operator_name`. */
export const principalName = (principal) => (
  principal?.kind === PORTAL_USER_KINDS.OPERATOR ? principal.operator_name : principal?.driver_name
) || '';

// The portal only ever lists Confirmed and Completed trips (the API's
// PORTAL_TRIP_STATUSES), and both read as good news.
const STATUS_HU = {
  Confirmed: 'Megerősítve',
  Completed: 'Teljesítve',
};

export const statusLabel = (status) => STATUS_HU[status] || status || '';

/**
 * Driver progress, reusing the dashboard's existing `driver_status.state`
 * vocabulary (see src/Utils/getDriverStatusLabel.js):
 *   1 departed to pickup · 2 arrived · 3 departed to dropoff · 4 finished
 */
export const PROGRESS_STEPS = ['Úton', 'Megérkeztem', 'Utas a kocsiban', 'Letettem'];

export const progressIndex = (trip) => {
  const state = Number(trip?.driver_status?.state);
  if (!Number.isFinite(state) || state < 1) return 0;
  return Math.min(state, PROGRESS_STEPS.length);
};

export const isFinished = (trip) => progressIndex(trip) >= PROGRESS_STEPS.length
  || trip?.status === 'Completed';

/**
 * True once the booking is no longer Confirmed — in practice Completed, the
 * only other status the portal lists. The office routinely marks a trip
 * Completed before the driver taps "Letettem", and from then on the API
 * refuses every step (the passenger terminal records steps on a Confirmed
 * booking only). A trip with no status at all is treated as open: the API
 * has the final word on the tap.
 */
export const isTripClosed = (trip) => Boolean(trip?.status) && trip.status !== 'Confirmed';

// DriverEvents.DRIVER_SUBMITTED_CUSTOMER_NO_SHOW.
const STATE_NO_SHOW = 5;

/** True once the driver has reported that the passenger did not turn up. */
export const isNoShow = (trip) => Number(trip?.driver_status?.state) === STATE_NO_SHOW;

/**
 * The event each advance tap sends, in order. Index = the current
 * `progressIndex`, so a trip at state 2 (arrived) sends departed_to_dropoff.
 */
const TRIP_EVENTS = [
  'departed_to_pickup',
  'arrived_at_pickup',
  'departed_to_dropoff',
  'arrived_at_dropoff',
];

/** What the advance button says at each step — the driver's own words. */
export const ADVANCE_LABELS = ['Úton vagyok', 'Megérkeztem', 'Utas a kocsiban', 'Letettem'];

/** The next event for this trip, or '' once it has been dropped off or closed. */
export const nextTripEvent = (trip) => (isTripClosed(trip) ? '' : TRIP_EVENTS[progressIndex(trip)] || '');

// DriverEvents.DRIVER_ARRIVED_AT_PICKUP.
const STATE_ARRIVED = 2;

/** True while the driver is standing at the pickup waiting for the guest. */
export const isWaitingAtPickup = (trip) => Number(trip?.driver_status?.state) === STATE_ARRIVED;

/** Milliseconds as '04:07' or, past an hour, '1:04:07'. Never negative. */
export const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return hours ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
};

/** Elapsed ms from an ISO date (or Date) to `now`; 0 when the date is unusable. */
export const elapsedSince = (value, now = Date.now()) => {
  const start = new Date(value || NaN).valueOf();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, new Date(now).valueOf() - start);
};

/* --- overtime ----------------------------------------------------------- */
// The trip carries `driver_overtime` (see src/Utils/driverOvertime.js).
// Minutes only — never money.

// DriverEvents.DRIVER_DEPARTED_TO_DROPOFF / DRIVER_ARRIVED_AT_DROPOFF.
const STATE_IN_CAR = 3;
const STATE_DROPPED_OFF = 4;

export const OVERTIME_MIN_MINUTES = 1;
export const OVERTIME_MAX_MINUTES = 600;
export const OVERTIME_NOTE_MAX = 300;

export const OVERTIME_STAGE_LABELS = { pickup: 'Felvételnél', dropoff: 'A célnál' };
export const OVERTIME_STAGES = ['pickup', 'dropoff'];
export const OVERTIME_METHOD_LABELS = { timer: 'Időzítve', manual: 'Utólag jelentve' };

/** True while the guest is in the car, on the way to the drop-off. */
export const isInCar = (trip) => Number(trip?.driver_status?.state) === STATE_IN_CAR;

/** True once the driver has tapped "Letettem". */
export const isDroppedOff = (trip) => Number(trip?.driver_status?.state) === STATE_DROPPED_OFF;

// DriverEvents.DRIVER_DEPARTED_TO_PICKUP — where the journey begins.
const STATE_DEPARTED = 1;

/**
 * IS THE JOURNEY UNDER WAY? — the one notion of "this trip is happening
 * right now", and the window a live location is reported in.
 *
 * It opens on "Úton vagyok" and closes on "Letettem": exactly the span in
 * which "where is my driver" is a question anybody is asking. Before it the
 * driver has not set off; after it the trip is over and where the phone is
 * stops being anyone's business.
 *
 * Built from the SAME `driver_status.state` the progress strip, the waiting
 * panel and the overtime gates are built from — there is deliberately no
 * second idea of an active trip on this screen. And from `isTripClosed` for
 * the office's half: a trip the office has marked Completed takes no more
 * driver input, position included, and the API refuses one.
 *
 * A no-show is NOT under way. It is reached from the pickup rather than
 * through the drop-off, so a range test (`state < 4`) would have let it
 * through, and the trip has ended either way.
 *
 * The API holds the same window (`PortalTripEventService`'s
 * LIVE_LOCATION_STATES) and refuses a report outside it; this is what stops
 * the phone asking.
 */
export const isJourneyUnderWay = (trip) => {
  const state = Number(trip?.driver_status?.state);

  return state >= STATE_DEPARTED
    && state <= STATE_IN_CAR
    && !isTripClosed(trip);
};

/**
 * Whether the API takes an `overtime_reported` for this trip: only on a
 * Confirmed booking, once "Letettem" is on record. A Completed trip is
 * closed — by the office or the nightly auto-complete — and takes no more
 * driver input, overtime included: the driver reports before it closes.
 */
export const canReportOvertime = (trip) => trip?.status === 'Confirmed' && isDroppedOff(trip);

// The shared reading of the list (src/Utils/driverOvertime.js mirrors the
// API's own helper) — the portal adds only the per-stage view its panel needs.
const stageTimers = (trip, stage) => overtimeEntries(trip?.driver_overtime)
  .filter((entry) => entry.method === 'timer' && entry.stage === stage);

/**
 * Overtime for one stage as the panel needs it: 'none' → no timer yet,
 * 'running' → a timer is open, 'ended' → a timer was closed.
 */
export const overtimePhase = (trip, stage) => {
  const timers = stageTimers(trip, stage);
  if (timers.some(isRunningTimer)) return 'running';
  return timers.length ? 'ended' : 'none';
};

/** Minutes of the closed timers at one stage — for the panel's summary line. */
export const stageTimerMinutes = (trip, stage) => overtimeTotalMinutes(stageTimers(trip, stage));

// PortalTripEventService's answer to "Túlóra vége" when no timer is open —
// the server closed it already ("Utas a kocsiban" does), or an earlier tap
// landed but its response did not.
const OVERTIME_NOT_STARTED = 'Overtime has not been started';

// The passenger terminal's refusal of a step on a booking that is no longer
// Confirmed — the office closed the trip while this screen was open.
const RESERVATION_NOT_CONFIRMED = 'Reservation is not Confirmed';

/** The API's own (English) reasons in a 400's `{ errors: [{ field, message }] }`. */
const refusalReasons = (error) => (error?.response?.status === 400
  ? (error.response.data?.errors || []).map((issue) => issue?.message)
  : []);

/** True when an `overtime_ended` was refused because the timer is already stopped. */
export const isOvertimeAlreadyStopped = (error) => refusalReasons(error)
  .includes(OVERTIME_NOT_STARTED);

/** True when a step was refused because the office has closed the trip meanwhile. */
export const isTripClosedRefusal = (error) => refusalReasons(error)
  .includes(RESERVATION_NOT_CONFIRMED);

/**
 * Validates what the driver typed into the "Volt túlóra?" minutes field.
 * Whole minutes only, 1–600; anything else is NaN.
 */
export const parseOvertimeMinutes = (value) => {
  const cleaned = String(value ?? '').replace(/[\s\u00a0\u202f]/g, '');
  if (!/^\d+$/.test(cleaned)) return NaN;
  const minutes = Number(cleaned);
  return minutes >= OVERTIME_MIN_MINUTES && minutes <= OVERTIME_MAX_MINUTES ? minutes : NaN;
};

/* --- expenses ----------------------------------------------------------- */
// The only amounts that ever appear on the portal: what the driver typed in
// as their own out-of-pocket claim. Never a booking price.

export const EXPENSE_TYPES = [
  { value: 'parking', label: 'Parkolójegy' },
  { value: 'other', label: 'Egyéb' },
];

export const EXPENSE_CURRENCIES = ['HUF', 'EUR'];

/** The confirmation shown once the sheet has closed on a successful submit. */
export const EXPENSE_SAVED_MESSAGE = 'Költség rögzítve — beküldtük jóváhagyásra.';

export const expenseTypeLabel = (type) => EXPENSE_TYPES
  .find((option) => option.value === type)?.label || 'Egyéb';

// DriverExpenseService's three states. A status added later reads as
// "Beküldve" until the portal learns it, rather than breaking the list.
const EXPENSE_STATUS = {
  submitted: { label: 'Beküldve', tone: 'neutral' },
  approved: { label: 'Elfogadva', tone: 'ok' },
  rejected: { label: 'Elutasítva', tone: 'bad' },
};

export const expenseStatus = (status) => EXPENSE_STATUS[status] || EXPENSE_STATUS.submitted;

/* --- what the photo said ------------------------------------------------ */
/**
 * `POST /portal/expenses/scan` reads the receipt and answers `readable:
 * false` with `unreadable_reasons` — a CLOSED vocabulary owned by the API.
 *
 * This turns those codes into the driver's own words, and it is deliberately
 * tolerant: a code this file has never heard of falls back to the generic
 * line rather than rendering as `total_illegible`. The vocabulary lives on
 * the other side of a contract and WILL grow; a driver in a car park at 1am
 * must not be the one who finds out it did.
 *
 * `scan_unavailable` is in the vocabulary and means the model never ran. It
 * is worded as "nem sikerült beolvasni" — exactly like a bad photo — on
 * purpose: the driver cannot act on the difference between a blurry photo and
 * a service that was down, and the only honest instruction in both cases is
 * "try another photo, or send it anyway". The DISPATCHER is told which it was
 * (Utils/tripClaims's receiptScanBadge); the driver is not.
 *
 * Shared with the operator screens, which pass their own English `words`.
 */
export const scanReasonLines = (codes, words, fallback) => {
  const lines = [...new Set(codes || [])]
    .map((code) => words[code])
    .filter(Boolean);
  return lines.length ? lines : [fallback];
};

const SCAN_REASONS_HU = {
  blurry: 'A fotó túl homályos.',
  total_illegible: 'A végösszeget nem lehet kiolvasni.',
  cut_off: 'A blokk egy része kimaradt a képből.',
  too_dark: 'A fotó túl sötét.',
  not_a_receipt: 'Ezen a képen nem látszik blokk.',
  scan_unavailable: 'Ezt a fotót most nem sikerült beolvasni.',
};

const SCAN_REASON_FALLBACK_HU = 'Ezt a fotót nem sikerült beolvasni.';

/**
 * Parses what a driver types on a phone keypad: '12 500', '12500', '25,50',
 * '25.50'. Returns NaN for anything that is not a positive amount.
 */
export const parseAmount = (value) => {
  const cleaned = String(value ?? '')
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return NaN;
  const amount = Number(cleaned);
  return amount > 0 ? amount : NaN;
};

/**
 * '12 500' / '25,50' — Hungarian digit grouping, hand-rolled because no Intl
 * locale produces exactly that on every phone. The spaces are non-breaking,
 * so an amount never wraps mid-number. Shared with the operator screens.
 */
export const groupDigits = (value, decimals) => {
  const [whole, fraction] = Number(value).toFixed(decimals).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  return fraction ? `${grouped},${fraction}` : grouped;
};

/** '12 500 HUF' / '25,50 EUR' — the driver's own expense claim. */
export const formatAmount = (amount, currency = 'HUF') => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return `${groupDigits(n, currency === 'HUF' ? 0 : 2)}\u00a0${currency}`;
};

/** 'Ma 14:20' / 'Tegnap 09:05' / 'szeptember 12. 18:40' in Budapest time. */
export const expenseWhen = (value, now = new Date()) => {
  if (!value) return '';
  return `${dayLabel(value, now)} ${formatTime(value)}`;
};

/* --- the office ---------------------------------------------------------- */
// Drivers reach the office on WhatsApp only (config PH_OFFICE_WHATSAPP) —
// no screen ever tells a driver to call.

export const OFFICE_WHATSAPP_HINT = 'Írj az irodának WhatsAppon.';

/* --- errors ------------------------------------------------------------- */

/**
 * Turns an axios error carrying the API's standard `{ message, errors }`
 * envelope into one sentence from `copy`. The API's own message is English
 * and meant for logs, so the status code decides the sentence:
 * `copy.network` when no answer came back at all, `copy[status]`, then
 * `copy.server` for any 5xx, else `copy.fallback`. A 400 whose reason is
 * listed in `copy.reasons` (API message → sentence) says that instead: some
 * refusals the driver can do nothing about deserve their own words. One
 * reading for both portals — each supplies its own language.
 */
export const errorMessage = (error, copy) => {
  const status = error?.response?.status;
  if (!error?.response) return copy.network;
  const reason = refusalReasons(error).find((message) => copy.reasons?.[message]);
  if (reason) return copy.reasons[reason];
  if (copy[status]) return copy[status];
  if (status >= 500) return copy.server;
  return copy.fallback;
};

const HU_ERRORS = {
  network: 'Nincs kapcsolat a szerverrel. Próbáld újra, amint van térerő.',
  400: 'Hiányzó vagy hibás adat. Ellenőrizd, és próbáld újra.',
  401: 'A belépésed lejárt. Kérj új belépési linket az irodától.',
  403: `Ehhez nincs jogosultságod. ${OFFICE_WHATSAPP_HINT}`,
  404: 'Ez a fuvar már nem szerepel a beosztásodban.',
  409: 'Ez a lépés már rögzítve van, vagy közben változott a fuvar állapota.',
  413: 'A fotó túl nagy. Készíts egy új fotót.',
  server: 'Hiba történt a szerveren. Próbáld újra egy perc múlva.',
  fallback: 'Valami nem sikerült. Próbáld újra.',
  reasons: {
    [RESERVATION_NOT_CONFIRMED]: 'Ezt a fuvart az iroda közben lezárta.',
  },
};

/** The driver screens' sentence; `overrides` lets a screen be more specific. */
export const portalErrorMessage = (error, overrides = {}) => errorMessage(error, {
  ...HU_ERRORS,
  ...overrides,
});

const HU_LOAD_FAILED = 'Most nem sikerült betölteni. Próbáld újra később.';

/**
 * The same for a screen that only reads. Only a real network failure (no
 * answer at all) says `network` — "check your connection". A refused login
 * (403), a server fault and an ended session keep their own sentences; the
 * statuses that only mean something for a write read as "not now".
 */
export const portalLoadErrorMessage = (error, network) => portalErrorMessage(error, {
  network,
  400: HU_LOAD_FAILED,
  404: HU_LOAD_FAILED,
  409: HU_LOAD_FAILED,
  413: HU_LOAD_FAILED,
  fallback: HU_LOAD_FAILED,
});

/**
 * Splits a place's address into a headline and the rest: 'Hotel Gellért' /
 * 'Budapest, …'. Takes a point (`{ address }`) or a bare address string.
 */
export const place = (point) => {
  const address = String((typeof point === 'string' ? point : point?.address) || '').trim();
  const [head, ...rest] = address.split(',');
  return {
    title: head.trim(),
    detail: rest.join(',').trim(),
    address,
  };
};

/** A head count from the API: a positive number, else 0. */
export const positiveCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Child seats, named exactly as the booking site shows them to the customer
 * (web-mono ui-next messages/hu/booking.json), so the driver loads the seat
 * the customer actually picked. One table for chips and details alike.
 */
export const CHILD_SEATS = [
  { field: 'booster_seat', name: 'ülésmagasító', range: '4–10 év, 22–36 kg' },
  { field: 'convertible_seat', name: 'átalakítható ülés', range: '1–4 év, 9–25 kg' },
  { field: 'infant_carrier', name: 'csecsemőhordozó', range: '0–1 év, max. 10 kg' },
];

/** The named passenger list — `[{ name, flight_number?, notes? }]`, may be absent. */
export const passengerManifest = (trip) => trip?.passenger_manifest || [];

export const totalPassengers = (trip) => {
  const p = trip?.passengers || {};
  return positiveCount(p.adults) + positiveCount(p.children) || passengerManifest(trip).length;
};

/** Short chips for the hero card: ['2 utas', '1 ülésmagasító']. */
export const passengerChips = (trip) => {
  const p = trip?.passengers || {};
  const chips = [];
  const people = positiveCount(p.adults) + positiveCount(p.children);
  if (people) chips.push(`${people} utas`);
  CHILD_SEATS.forEach(({ field, name }) => {
    if (positiveCount(p[field])) chips.push(`${positiveCount(p[field])} ${name}`);
  });
  return chips;
};

// How passengerSummary words a head count and a seat. The operator screens
// pass their English (operatorFormat's PASSENGER_WORDS_EN), same shape.
const PASSENGER_WORDS = {
  adults: (n) => `${n} felnőtt`,
  children: (n) => `${n} gyerek`,
  seats: CHILD_SEATS,
  seat: (n, { name, range }) => `${n} ${name} (${range})`,
};

/** '2 felnőtt · 1 ülésmagasító (4–10 év, 22–36 kg)' for the details table. */
export const passengerSummary = (trip, words = PASSENGER_WORDS) => {
  const p = trip?.passengers || {};
  const parts = [];
  if (positiveCount(p.adults)) parts.push(words.adults(positiveCount(p.adults)));
  if (positiveCount(p.children)) parts.push(words.children(positiveCount(p.children)));
  words.seats.forEach((seat) => {
    if (positiveCount(p[seat.field])) parts.push(words.seat(positiveCount(p[seat.field]), seat));
  });
  return parts.join(' · ');
};

/** The programme's stops (ph-core ProgrammeStop) in `order` — they may arrive out of sequence. */
export const programmeStops = (trip) => [...(trip?.programme?.stops || [])]
  .sort((a, b) => a.order - b.order);

/**
 * The trip's programme, whatever shape it is in — the ONE place the map card,
 * the programme card and the day list read it from, so they cannot disagree
 * about what a programme is.
 *
 * DELIBERATELY NOT GATED ON `programme.enabled`. That switch is labelled "Add
 * intermediate stops" in Orders and the office uses it for exactly that: of
 * the 36 bookings in six months carrying hours with the switch off, all 36
 * were real work — Tripster and Sputnik8 guided tours, "Coordinator services",
 * an F1 daily disposal, a helicopter charter — and nine of them had stops too.
 * Not one was a leftover. The hours and the name are what the office fills in;
 * they are the programme.
 */
const liveProgramme = (trip) => trip?.programme || null;

/** The programme's booked length in hours; 0 when the trip has no programme length. */
export const programmeHours = (trip) => {
  const hours = Number(liveProgramme(trip)?.total_hours);
  return hours > 0 ? hours : 0;
};

/** The programme's own name, '' when it has none. */
export const programmeName = (trip) => liveProgramme(trip)?.name || '';

/**
 * Is this trip booked as a programme? BOTH shapes are real and both mean the
 * same thing to the driver — they have the car for the day, not for one A→B
 * drive: a set itinerary, and a name and/or a number of hours with no stops
 * planned yet. Only the second used to be invisible.
 */
export const hasProgramme = (trip) => Boolean(
  programmeStops(trip).length || programmeHours(trip) || programmeName(trip),
);

/** When the driver leaves a programme stop: its departure, else arrival + stay, else arrival. */
const stopLeftAt = (stop) => {
  const at = (value) => (value ? new Date(value).valueOf() : NaN);
  if (Number.isFinite(at(stop.departure_time))) return at(stop.departure_time);
  const arrives = at(stop.arrival_time);
  return arrives + (Number(stop.duration_minutes) > 0 ? stop.duration_minutes * 60000 : 0);
};

/**
 * Where the Navigáció buttons and the map send the driver, as
 * `{ point, label }`: the pickup until the passenger is aboard ("Utas a
 * kocsiban"); after that the drop-off — or, on a programme, the first stop
 * not yet left behind. The payload has no per-stop progress, so "left
 * behind" is the stop's planned time; a stop with no time is skipped.
 */
export const navigationTarget = (trip, now = new Date()) => {
  if (!(Number(trip?.driver_status?.state) >= STATE_IN_CAR)) {
    return { point: trip?.from, label: 'Felvétel' };
  }
  const stops = programmeStops(trip);
  const nowMs = new Date(now).valueOf();
  const next = stops.findIndex((stop) => stopLeftAt(stop) > nowMs);
  if (next !== -1 && next < stops.length - 1) return { point: stops[next], label: 'Következő megálló' };
  return { point: next === -1 ? trip?.to : stops[next], label: 'Cél' };
};

/* --- the customer's phone ------------------------------------------------ */
// The API sends `customer_phone` only from 24 hours before pickup to 24 hours
// after it. Too early, it sends `customer_phone_visible_from` (pickup − 24h)
// instead; outside the window otherwise, neither.

export const PHONE_RELEASE_HINT = 'Az utas telefonszáma az indulás előtt 24 órával jelenik meg.';

/** When a withheld number is released (ISO), or '' when none is pending. */
export const phoneReleaseAt = (trip) => (
  trip?.customer_phone ? '' : trip?.customer_phone_visible_from || ''
);

/**
 * Whether the name sign may carry Pickup Hungary branding. Some partners
 * resell under their own name and a PH-branded sign in arrivals breaks the
 * white-label deal — so the trip carries the decision, and only an explicit
 * `true` brands it.
 */
export const brandingAllowed = (trip) => trip?.ph_branding_allowed === true;

/**
 * The route services give km to one decimal: '24 km', or '8,4 km' when short
 * — `decimalMark` '.' for the English screens.
 */
export const formatDistance = (km, decimalMark = ',') => {
  const n = Number(km);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 10) return `${Math.round(n)} km`;
  return `${String(Math.round(n * 10) / 10).replace('.', decimalMark)} km`;
};

/**
 * A programme's booked hours for display: '6', or '4,5' when it is not a whole
 * number of them — `decimalMark` '.' for the English screens, as formatDistance
 * does. Never "about": the hours were sold, they are not an estimate.
 */
export const formatHours = (hours, decimalMark = ',') => (
  String(Math.round(Number(hours) * 10) / 10).replace('.', decimalMark)
);

// formatDuration's words; the English screens pass their own (same keys).
const DURATION_UNITS = { about: 'kb.', hours: 'óra', minutes: 'perc' };

/** The route services give whole minutes: 'kb. 32 perc' / 'kb. 1 óra 5 perc'. */
export const formatDuration = (minutes, units = DURATION_UNITS) => {
  const n = Math.round(Number(minutes));
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 60) return `${units.about} ${n} ${units.minutes}`;
  const rest = n % 60;
  return `${units.about} ${Math.floor(n / 60)} ${units.hours}${rest ? ` ${rest} ${units.minutes}` : ''}`;
};

/** Sorts by pickup time, oldest first. Non-mutating. */
export const byTime = (trips = []) => [...trips].sort(
  (a, b) => new Date(a?.date_datetime || 0) - new Date(b?.date_datetime || 0),
);

/**
 * The trip the driver should be looking at: the first one that has not been
 * dropped off yet, preferring anything still ahead of "now".
 */
export const nextTrip = (trips = [], now = new Date()) => {
  const ordered = byTime(trips).filter((trip) => !isFinished(trip));
  const ahead = ordered.find((trip) => new Date(trip?.date_datetime || 0) >= new Date(now));
  return ahead || ordered[0] || null;
};

/* --- copy for the shared trip components ---------------------------------
 * The driver screens' Hungarian for the components the Operator Admin's trip
 * details reuse (PortalRouteMap, TripCards, OvertimeCard, RecordExpense /
 * ExpenseSheet / ExpenseList). Each component takes a `copy` prop that
 * defaults to the object here; operatorFormat.js holds the English twin of
 * each, with the same keys. One component, two languages — never a fork.
 */

/**
 * DayPickerSheet — the month view behind the week strip's calendar button.
 *
 * `locale` is the moment locale react-datetime names the days and months
 * with. It is a copy field rather than a constant in the component because
 * it IS the language: the office's twin (operatorFormat's
 * DAY_PICKER_COPY_EN) asks for moment's own English. See the component for
 * why Hungarian has to be loaded the way it is.
 */
export const DAY_PICKER_COPY = {
  title: 'Válassz napot',
  closeLabel: 'Bezárás',
  locale: 'hu',
  trips: (n) => `, ${n} út`,
};

/**
 * PushSetting — the driver's words for phone notifications, in all three
 * places it appears (the enrolment screen, the one-time ask on the day, and
 * the plain switch in the menu).
 *
 * `unavailable` is one sentence per reason push CANNOT work here, and each
 * of them is true: the component never shows a switch that would flip and do
 * nothing. The office's twin is operatorFormat's PUSH_COPY_EN.
 */
export const PUSH_COPY = {
  head: 'Értesítések ezen a telefonon',
  invite: 'Kérsz értesítést?',
  onHead: 'Értesítések bekapcsolva',
  /** What a driver would be told about, in the order they will meet it. */
  what: 'Szólunk, ha új fuvart kapsz, ha valami változik rajta, '
    + 'ha hamarosan indulnod kell, és ha az iroda elbírálta a költséged.',
  onBody: (what) => `Bekapcsolva. ${what}`,
  enable: 'Értesítések bekapcsolása',
  enabling: 'Bekapcsolás…',
  disable: 'Értesítések kikapcsolása',
  disabling: 'Kikapcsolás…',
  later: 'Most nem',
  laterHint: 'Bármikor bekapcsolhatod a profil menüben.',
  unavailable: {
    [PUSH_SUPPORT.IOS_NEEDS_HOME_SCREEN]: 'iPhone-on csak akkor működik, ha az alkalmazás '
      + 'ki van téve a főképernyőre. Tedd ki, nyisd meg az ikonról, és itt bekapcsolhatod.',
    [PUSH_SUPPORT.DENIED]: 'Korábban letiltottad az értesítéseket. A telefon beállításaiban '
      + 'tudod újra engedélyezni — a böngésző már nem kérdez rá.',
    [PUSH_SUPPORT.UNSUPPORTED]: 'Ez a böngésző nem tud értesítést küldeni. '
      + 'Nyisd meg az alkalmazást a főképernyős ikonról.',
    [PUSH_SUPPORT.NOT_CONFIGURED]: 'Az értesítések most nem elérhetők. '
      + 'Ettől még minden fuvarod itt van az alkalmazásban.',
  },
};

/**
 * LIVE LOCATION, IN THE DRIVER'S OWN WORDS — a statement, not an offer.
 *
 * Position sharing is on for every driver during a journey and there is no
 * switch, so this copy has one job: make sure no driver can be surprised by
 * it. It is not asking, and it must not read as though it were — an "off"
 * framing next to a control that does not exist would be worse than saying
 * nothing.
 *
 * What it still says, because all of it remains true and all of it is the
 * transparency this rests on:
 *
 *  1. WHO SEES IT. "A megrendelő partner" — not "the system", not "we". The
 *     position goes to the company that sold the trip so they can tell the
 *     passenger where the car is. That is the actual reason, and a driver is
 *     entitled to it.
 *  2. THAT IT IS ONLY THIS JOURNEY, and that it ends by itself at the
 *     drop-off. A driver should not be left wondering whether their phone is
 *     reporting between jobs, or after a shift. It is not, and nothing in
 *     this app has ever reported that.
 *
 * It does NOT promise the feed is continuous, because it is not: this is a
 * web app and reporting stops whenever the trip screen is not in front of
 * the driver (useLiveLocation). Saying "we always know where you are" would
 * be the one claim here that is false.
 */
export const LIVE_LOCATION_COPY = {
  /** On the trip screen, while it is actually sending. */
  live: 'A fuvar végéig megosztjuk a helyzetedet a megrendelő partnerrel',
  /** The second line: why, and when it ends. */
  liveDetail: 'Így tudja megmutatni az utasnak, merre jár az autó. '
    + 'A letételnél magától leáll, fuvarok között nem küldünk helyzetet.',
};

/**
 * The two ends of a trip, in the driver's words — the route map's summary, the
 * route rail's labels and the programme card's stand-in rail all name them, and
 * they must be the same two words everywhere. The operator screens' twin is
 * operatorFormat's ENDPOINT_LABELS_EN.
 */
export const ENDPOINT_WORDS = { from: 'Felvétel', to: 'Cél' };

/**
 * The word on the "now" line across the driver's day (Components/NowLine).
 * The staff screens' twin is operatorFormat's NOW_LABEL_EN.
 */
export const NOW_LABEL = 'Most';

/** PortalRouteMap. */
export const ROUTE_MAP_COPY = {
  route: 'Útvonal',
  pickup: ENDPOINT_WORDS.from,
  dropoff: ENDPOINT_WORDS.to,
  stops: (n) => `${n} megállóval`,
  navigate: 'Navigáció',
  distance: (km) => formatDistance(km),
  duration: (minutes) => formatDuration(minutes),
  /**
   * A programme's booked length, where the drive time would otherwise be. It
   * names the programme and drops the "kb." on purpose: the routing estimate
   * is a guess about one A→B leg, a ten-hour tour is neither.
   */
  programmeDuration: (hours) => `${formatHours(hours)} órás program`,
};

/** TripCards: the programme, the manifest and the customer. */
export const TRIP_CARD_COPY = {
  programme: 'Program',
  programmeUnnamed: 'Program megállókkal',
  // The same sentence for the other real shape: hours booked, no itinerary.
  programmeNoStops: 'Program megállók nélkül',
  endpoints: ENDPOINT_WORDS,
  hours: (n) => `${formatHours(n)} óra`,
  stop: 'Megálló',
  stay: (n) => `${n} perc`,
  passengers: 'Utasok',
  people: (n) => `${n} fő`,
  flight: (flight) => `Járat: ${flight}`,
  phoneHint: () => PHONE_RELEASE_HINT,
  call: 'Hívás',
};

/** OvertimeCard. */
export const OVERTIME_COPY = {
  title: 'Túlóra',
  stages: OVERTIME_STAGE_LABELS,
  methods: OVERTIME_METHOD_LABELS,
  running: 'Fut',
  minutes: (n) => `${n} perc`,
  total: (n) => `Összesen: ${n} perc`,
  // The office reviews every entry like a receipt — the same wording:
  // Beküldve / Elfogadva / Elutasítva (an entry's 'pending' is 'Beküldve').
  status: expenseStatus,
};

/** RecordExpense, ExpenseSheet and ExpenseList. */
export const EXPENSE_COPY = {
  record: 'Költség rögzítése',
  recordForTrip: 'Költség rögzítése ehhez az úthoz',
  saved: EXPENSE_SAVED_MESSAGE,
  title: 'Költség rögzítése',
  forTrip: (label) => `Ehhez az úthoz: ${label}`,
  closeLabel: 'Bezárás',
  typeLabel: 'Típus',
  types: EXPENSE_TYPES,
  amountLabel: 'Összeg',
  currencyLabel: 'Pénznem',
  photoAlt: 'A blokk fotója',
  photoReady: 'Fotó kész',
  retake: 'Új fotó',
  processing: 'Feldolgozás…',
  pickPhoto: 'Fotó a blokkról',
  processingPhoto: 'Fotó feldolgozása…',
  noteLabel: 'Megjegyzés',
  optional: ' · nem kötelező',
  notePlaceholder: 'Pl. Terminál 2 parkolóház, 40 perc',
  submit: 'Beküldés',
  sending: 'Küldés…',
  // The BROWSER could not even decode the file (imageDownscale threw) — a
  // different thing from a photo the scan could not read, which gets the
  // `unreadable*` wording below and an escape hatch.
  photoUnreadable: 'Ezt a fotót nem sikerült beolvasni. Készíts egy újat.',
  amountMissing: 'Add meg az összeget (pl. 2500 vagy 12,50).',
  photoMissing: 'Fotózd le a blokkot vagy a parkolójegyet.',

  /* --- the reading off the photo --------------------------------------- */
  scanning: 'Blokk beolvasása…',
  scanTitle: 'A fotó alapján',
  scanAmountLabel: 'Összeg',
  scanDateLabel: 'Dátum',
  scanPlaceLabel: 'Hely',
  // Each field of `read` is nullable on its own, so each absence is said out
  // loud. A blank row would read as "nothing was found", which is wrong when
  // the total was read and only the merchant was not.
  scanMissing: 'nem olvasható',
  // The hint under the amount. It says the field is CHANGEABLE, because it
  // is: the driver's typed value is what gets submitted, always.
  scanHint: 'A fotó alapján. Írd át, ha nem stimmel.',
  amountPlaceholder: 'Írd be a végösszeget',
  // While the scan is in flight: the placeholder says the total is on its way
  // and the submit says why it is waiting. Both exist because the submit is
  // DISABLED for those ~1-2s (see ExpenseSheet's `submit`), and a disabled
  // button whose label has not changed reads as a broken button.
  amountScanning: 'Olvasom a végösszeget…',
  scanWait: 'Beolvasás…',

  /* --- the photo could not be read ------------------------------------- */
  unreadableTitle: 'Ezt a fotót nem tudom elolvasni',
  unreadableReasons: (codes) => scanReasonLines(codes, SCAN_REASONS_HU, SCAN_REASON_FALLBACK_HU),
  retakePhoto: 'Új fotó készítése',
  // The quiet way out. It must ALWAYS be reachable: losing the receipt
  // altogether to save a typo is the worse outcome, and a driver whose
  // genuinely crumpled blokk the scan cannot read is not a problem to solve
  // by refusing their expense.
  sendAnyway: 'Beküldöm így is',
  sendAnywayNote: 'Az iroda látni fogja, hogy ezt nem sikerült beolvasni.',

  /* --- the same photo again -------------------------------------------- */
  duplicateTitle: 'Lehet, hogy ezt már beküldted',
  duplicateWhen: (when) => `Beküldve: ${when}`,
  // The match is on the PHOTO (photo_sha256), not on the place and the total.
  // The mockup said "same place, same total"; that would be a lie on a
  // receipt whose merchant was never read, and a dispatcher who catches the
  // badge lying once stops reading the column. Say what was actually matched.
  duplicateSame: 'Ugyanez a fotó.',
  duplicateDifferent: 'Ez egy másik blokk',
  duplicateCancel: 'Mégsem — ez ugyanaz',
  submitError: (failure) => portalErrorMessage(failure, {
    404: 'Ez a fuvar már nem szerepel a beosztásodban, így nem köthető hozzá költség.',
  }),
  listTitle: 'Rögzített költségek',
  count: (n) => `${n} db`,
  more: (n) => `+${n} korábbi költség`,
  when: expenseWhen,
  typeName: expenseTypeLabel,
  status: expenseStatus,
  amount: formatAmount,
  // 'Úthoz: PU_DASH_…' when the trip is one the screen has loaded, else
  // 'Úthoz kötve'; '' for a free-standing claim.
  tripText: (expense, trips) => {
    if (!expense.booking_id) return '';
    // eslint-disable-next-line no-underscore-dangle
    const trip = (trips || []).find((row) => row?._id === expense.booking_id);
    return trip?.booking_reference ? `Úthoz: ${trip.booking_reference}` : 'Úthoz kötve';
  },
};

/* --- free waiting & the flight ------------------------------------------ */
/**
 * How long the driver may wait at the pickup before the wait becomes
 * billable overtime, and — for an arriving passenger — how late the plane is.
 *
 * NOTHING IS COMPUTED HERE. The API sends an absolute window
 * (`free_waiting.starts_at` / `ends_at`, freeWaiting.helper) already
 * carrying the operator's minutes, the airport-or-not decision and any
 * credited flight delay; this file only reads the clock against it. That is
 * deliberate: a phone that worked out its own allowance could disagree with
 * what is actually billable, and a countdown that disagrees with the invoice
 * is worse than no countdown.
 *
 * The anchor is the SCHEDULED PICKUP TIME, not the moment the driver tapped
 * "Megérkeztem" — so arriving twenty minutes early leaves the allowance
 * untouched (`early`), and arriving late leaves only what is left of it.
 */

export const freeWaiting = (trip) => trip?.free_waiting || null;

const instant = (value) => {
  const at = new Date(value || NaN).valueOf();
  return Number.isFinite(at) ? at : NaN;
};

/**
 * Where the free waiting stands:
 *   'early'   — the driver is here before the pickup time; none of it spent
 *   'running' — inside the allowance
 *   'expired' — it has run out
 * '' when the trip carries no window (no pickup time to anchor to).
 */
export const freeWaitingPhase = (trip, now = Date.now()) => {
  const window = freeWaiting(trip);
  const starts = instant(window?.starts_at);
  const ends = instant(window?.ends_at);
  if (!Number.isFinite(starts) || !Number.isFinite(ends)) return '';

  const at = new Date(now).valueOf();
  if (at < starts) return 'early';
  return at < ends ? 'running' : 'expired';
};

/** Milliseconds of free waiting left; 0 once it is gone or unknown. */
export const freeWaitingRemaining = (trip, now = Date.now()) => {
  const ends = instant(freeWaiting(trip)?.ends_at);
  if (!Number.isFinite(ends)) return 0;
  return Math.max(0, ends - new Date(now).valueOf());
};

/** True while the flight's delay has been credited to the clock at all. */
export const freeWaitingHasDelay = (trip) => Number(freeWaiting(trip)?.delay_credited_minutes) > 0;

/**
 * True when the plane is later than the operator agreed to absorb. The clock
 * has still been moved (by the cap), but the driver is told to hand it to the
 * office rather than keep waiting on their own judgement.
 */
export const freeWaitingDelayCapped = (trip) => freeWaiting(trip)?.delay_capped === true;

export const flightStatus = (trip) => trip?.flight_status || null;

/** Whole minutes the arriving flight is late; 0 when on time or unknown. */
export const flightDelayMinutes = (trip) => {
  const minutes = Number(flightStatus(trip)?.delay_minutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

/**
 * Is there a flight to show at all? The number alone is worth a card — the
 * driver has always had it on the trip — and the live times join it when the
 * API managed to look them up.
 */
export const hasFlight = (trip) => Boolean(
  trip?.flight_number || flightStatus(trip)?.flight_number,
);

/**
 * Is this an AIRPORT pickup, as the API classified it (`free_waiting.airport`)?
 * The same trips it looks live flight status up for — so a card can tell
 * "we tried and the airline gave us nothing" apart from "there was never a
 * reason to ask", which is a transfer TO the airport.
 */
export const isAirportPickupTrip = (trip) => freeWaiting(trip)?.airport === true;

// The API looks a flight up only from this long before the pickup to this
// long after it (PortalFlightStatusService's own window). Re-reading a trip
// outside it would cost a request to be told the same thing every time.
const FLIGHT_WATCH_HOURS = 6;

/**
 * Should this trip re-read itself on a timer? Only one with a flight, still
 * going ahead, and close enough to its pickup that the API would look the
 * flight up at all — the delay is the one thing on the screen that changes
 * with nobody touching it, and it is the only reason to poll.
 */
export const pollsFlight = (trip, now = Date.now()) => {
  if (!hasFlight(trip) || isTripClosed(trip) || isDroppedOff(trip) || isNoShow(trip)) return false;

  const pickup = new Date(trip?.date_datetime || NaN).valueOf();
  if (!Number.isFinite(pickup)) return false;

  return Math.abs(new Date(now).valueOf() - pickup) <= FLIGHT_WATCH_HOURS * 60 * 60 * 1000;
};

/**
 * How long the API's own cache serves a live flight before it looks it up
 * again (TTL_LIVE in Cache/CacheItems/FlightLookup). It is the trip
 * screen's re-read beat, and the point past which an arrival on screen
 * stopped being something we can call current.
 */
export const LIVE_FLIGHT_TTL_MS = 5 * 60 * 1000;

/**
 * Should the screen admit that the arrival it is showing may have moved?
 *
 * Only when BOTH are true: the last attempt to refresh failed (`failedAt`
 * newer than `okAt`), and what is on screen has reached the age at which
 * the API would have looked the flight up again. A blip that is answered on
 * the next beat says nothing to the driver, and a read that worked says
 * nothing either.
 *
 * The comparison is `>=`, not `>`, and that is the whole point rather than
 * an off-by-one: the beat IS the TTL, so a beat that failed is by
 * definition a refresh we missed. On `>` the first missed beat landed
 * exactly on the boundary and said nothing, which is the one case this
 * exists for.
 *
 * It is measured from OUR last good read, not from the lookup, because
 * `flight_status` carries no timestamp of its own (flightStatus.helper) —
 * so the honest claim is "we have not been able to refresh this", never
 * "the airline said this at 14:05". That read could itself have been served
 * from a cache entry nearly a TTL old, which is the other reason not to
 * give the driver the benefit of the boundary.
 */
export const flightDataStale = (refresh, now = Date.now()) => {
  const okAt = Number(refresh?.okAt);
  const failedAt = Number(refresh?.failedAt);
  if (!Number.isFinite(okAt) || !Number.isFinite(failedAt) || failedAt <= okAt) return false;

  return new Date(now).valueOf() - okAt >= LIVE_FLIGHT_TTL_MS;
};

/** FlightCard — the flight, and how late it is. */
export const FLIGHT_COPY = {
  title: 'Járat',
  onTime: 'Időben',
  // The "delayed meter": the one number the driver acts on.
  delayed: (minutes) => `${minutes} perc késés`,
  scheduled: 'Menetrend szerint',
  expected: 'Várható érkezés',
  landed: 'Leszállt',
  // No revised estimate ever arrives for a cancelled flight, so without this
  // the card called it on time. See flightBadge.
  cancelled: 'Törölve',
  arrivalAirport: 'Érkezik',
  // Nothing came back from the airline, on a trip we DID ask about. Say so
  // rather than imply "on time" — and say nothing at all on a trip we never
  // had reason to ask about (a transfer to the airport).
  unknown: 'A járat állapotát most nem tudjuk lekérdezni.',
  // The times above are still the last ones we had, and they are still the
  // best guess — so they stay on screen. This only stops the driver reading
  // them as current when we know we have not been able to check.
  stale: 'Ezt nem sikerült frissíteni — az érkezés közben változhatott.',
};

/**
 * What the badge on the flight card should say, and how alarmed it should
 * look — ONE rule, because the card had three states' worth of copy and only
 * two branches, and the missing branch was the one that matters.
 *
 * A cancelled flight comes back with no revised estimate, so its delay is 0
 * and the old code called it "Időben": a green badge telling a driver to wait
 * at the gate for a plane that is not coming. The airline's own word decides
 * first; the delay only speaks for a flight that is actually operating.
 */
export const flightBadge = (trip, copy = FLIGHT_COPY) => {
  const status = flightStatus(trip);
  if (!status) return null;

  const word = String(status.status || '');
  if (/cancel/i.test(word)) return { text: copy.cancelled, tone: 'bad' };

  const delay = flightDelayMinutes(trip);
  if (delay > 0) return { text: copy.delayed(delay), tone: 'late' };

  // Landed and on time are both good news, but the driver acts on them
  // differently: one means go in, the other means keep waiting.
  if (/arriv|land/i.test(word)) return { text: copy.landed, tone: 'ok' };

  return { text: copy.onTime, tone: 'ok' };
};

/* --- writing to the office ----------------------------------------------
 * ONE MESSAGE SHAPE FOR EVERY WHATSAPP THE PORTAL OPENS.
 *
 * The office's number is the only way a driver reaches them, and until now
 * what arrived depended on which button was pressed: the free-waiting block
 * named the trip, and the day screen's "Írj az irodának" — the button a
 * driver actually reaches for mid-shift — opened an EMPTY box. The office
 * got "Szia" and had to ask who, which job, and what was wrong before it
 * could do anything.
 *
 * So every message now carries the same SPINE — the booking reference, the
 * driver, the pickup time and the pickup address, which together are enough
 * to find the job — with one sentence on top saying what is happening. The
 * sentence is the only thing that varies.
 *
 * Kept short on purpose: this is prefilled through a URL
 * (`whatsappUrl` → `encodeURIComponent`, which turns the newlines into
 * %0A), and a driver should be able to read what they are about to send
 * without scrolling.
 *
 * The two genuinely trip-less buttons are NOT routed through here — the
 * gate (there is no session yet, so there is no trip and no name) and the
 * iOS install help. A spine needs a trip.
 */

/**
 * The state the driver is writing from. One sentence each, in their own
 * voice, saying what has happened and — where the portal asked them to do
 * something first — that they have done it.
 */
export const OFFICE_SITUATIONS = {
  /** The allowance ran out and the passenger still has not come. */
  free_waiting_expired: 'lejárt az ingyenes várakozás.',
  /**
   * The flight is later than the operator absorbs. The screen that sends a
   * driver here tells them: "Indítsd el a várakozás mérőt, majd írj az
   * irodának WhatsAppon. No-show jelentést csak utána." — so the message
   * says they have done exactly that, and the office is not left wondering.
   */
  delay_capped: 'a járat késése meghaladja az ingyenes várakozást. '
    + 'Elindítottam a várakozás mérőt, és várok. No-show-t nem jelentek, amíg nem egyeztettünk.',
  /** About to file a no-show, and asking first — as step 4 requires. */
  no_show_permission: 'az utas nem jelent meg. Jelenthetem no-show-ként?',
  /** Step 3: the call was made from the app and nobody answered. */
  passenger_unreachable: 'hívtam az utast az alkalmazásból, nem érem el.',
  /** Step 1: the driver cannot stay where they said they would be. */
  cannot_reach_meeting_point: 'nem tudok a megbeszélt találkozási pontra állni. '
    + 'Mielőtt elmozdulok, egyeztetnék.',
  /** The day screen's plain button: no particular state, but still a trip. */
  none: '',
};

export const OFFICE_SITUATION_KEYS = Object.keys(OFFICE_SITUATIONS);

/**
 * The WhatsApp the office receives, as lines.
 *
 * `trip` may be absent (a driver with nothing on today writing from the day
 * screen): the spine then shrinks to whatever is known rather than printing
 * empty labels. Every part is dropped when it is missing, so no message
 * ever contains "Felvétel: undefined".
 */
export const officeMessage = ({ trip = null, driverName = '', situation = 'none' } = {}) => {
  const sentence = OFFICE_SITUATIONS[situation] ?? OFFICE_SITUATIONS.none;
  const reference = trip?.booking_reference || '';
  const pickup = trip?.from?.address || '';
  const at = trip?.date_datetime ? formatTime(trip.date_datetime) : '';

  return [
    // The reference leads the sentence, because that is the first thing
    // anyone in the office types into the dashboard.
    [reference, sentence].filter(Boolean).join(' '),
    driverName ? `Sofőr: ${driverName}` : '',
    [at && `Felvétel: ${at}`, pickup].filter(Boolean).join(' · '),
  ].filter(Boolean).join('\n');
};

/**
 * WaitingPanel's free-waiting block.
 *
 * `expired` and `capped` are the only two lines that tell the driver to DO
 * something, and what they tell them is what the portal already supports:
 * start the overtime timer (the button below them), message the office on
 * WhatsApp (OfficeWhatsAppButton — drivers never call), and only report a
 * no-show once the passenger really cannot be reached.
 */
export const FREE_WAITING_COPY = {
  label: 'Ingyenes várakozás',
  remaining: 'Hátralévő ingyenes idő',
  // Which of the two allowances this trip is on, so the number is explicable.
  allowance: (minutes, airport) => `${minutes} perc${airport ? ' (repülőtér)' : ''}`,
  /** Early: the allowance has not started, and that is in the driver's favour. */
  early: (startsAt) => `Korán érkeztél — az ingyenes várakozás ${startsAt}-kor indul, `
    + 'addig nem fogy.',
  until: (endsAt) => `Eddig tart: ${endsAt}`,
  /** Why the free time reaches past the pickup time the driver was given. */
  delayed: (minutes, endsAt) => `A járat ${minutes} percet késik, ennyivel később `
    + `indul az ingyenes várakozás. Eddig tart: ${endsAt}`,
  expired: (endsAt) => `Az ingyenes várakozás ${endsAt}-kor lejárt.`,
  expiredWhatNow: 'Indítsd el a túlórát, és írj az irodának, ha az utas nem jelentkezik. '
    + 'Csak akkor jelezd a meg nem jelenést, ha már nem éred el.',
  capped: (delay, max) => `A járat ${delay} percet késik. Ebből ${max} percet tudunk `
    + 'ingyen várni, a többit az irodával kell egyeztetni.',
  cappedWhatNow: 'Írj az irodának, mielőtt továbbmennél — ne dönts egyedül a várakozásról.',
  /**
   * The terms this booking is actually on, listed under the clock so the
   * driver can see WHICH allowance they are running and what the other one
   * would have been. Reference, not an alarm — it must not compete with the
   * expired and capped lines, which are the ones asking them to do
   * something.
   */
  termsTitle: 'Ennél a Partnernél:',
  /** No partner, or a partner nobody agreed terms with: the same house numbers. */
  termsTitleHouse: 'Általános feltételek:',
  termsStandard: (minutes) => `${minutes} perc várakozás nem reptéren`,
  termsAirport: (minutes) => `${minutes} perc várakozás reptéren`,
  /** What a late flight buys, and what still runs after it. */
  termsDelay: (maxDelay, after) => `${maxDelay} perc késés + ${after} perc várakozás`,
  termsHouseNote: 'Ehhez a foglaláshoz nincs külön partnermegállapodás — '
    + 'az általános feltételek érvényesek.',
  office: 'Írj az irodának',
  /**
   * What the WhatsApp chat opens with. Kept as a one-argument function so
   * FreeWaitingBlock's `copy` contract is unchanged; the composing is
   * `officeMessage`'s, below, and the sentence is this state's.
   */
  officeMessage: (trip, driverName) => officeMessage({
    trip, driverName, situation: 'free_waiting_expired',
  }),
  /**
   * The capped case is a different message, because the driver was asked to
   * do something first: start the waiting timer, THEN write, and file no
   * no-show until the office has answered. What arrives says they have.
   */
  officeMessageCapped: (trip, driverName) => officeMessage({
    trip, driverName, situation: 'delay_capped',
  }),
};

/* --- the no-show, and what has to be true before one may be sent -------- */
/**
 * A no-show is a COMMERCIAL CLAIM against the customer, not a trip status,
 * and the partner who sold the trip will contest it. So the sheet that files
 * one is a procedure — the office's own four steps — and everything below is
 * what the screen needs to run it.
 *
 * THE RULE, in the office's words:
 *   "REPÜLŐTÉRI FUVAR: 60 percet a járat tényleges landolása után kell
 *    várni. Ezután a kapcsolattartót kell hívni."
 *   "A 60 percet nem a tervezett érkezéstől és nem a foglalásban szereplő
 *    felvételi időtől, hanem a járat tényleges landolásától kell számítani."
 *   "Nem repülőtéri fuvar esetén előzetes egyeztetés nélkül ne jelents
 *    no-show-t."
 *
 * THIS IS A MIRROR, NOT THE RULE. The API enforces exactly the same thing on
 * the write (ph-core's noShowEvidence.helper + PortalTripEventService), and
 * a refusal there is what actually protects the claim. What is here exists
 * so the driver sees a countdown and a disabled button instead of a 400 —
 * the same arrangement `driverOvertime.js` has with its own API helper.
 *
 * UNLIKE FREE WAITING, this clock is computed on the phone rather than sent
 * as a window. Free waiting is billable and so must come from the server or
 * not exist; this one only decides when a button lights up, and it needs to
 * re-derive itself the moment a five-minute flight refresh lands a landing
 * time. Both read the same instant either way — `flight_status.actual_arrival`.
 */

/** Minutes to wait after the wheels touch down, at an airport. */
export const NO_SHOW_WAIT_MINUTES = 60;

const NO_SHOW_WAIT_MS = NO_SHOW_WAIT_MINUTES * 60 * 1000;

/** The three steps the driver confirms — the API's NO_SHOW_CHECKPOINTS. */
export const NO_SHOW_CHECKPOINTS = [
  'stayed_at_meeting_point',
  'checked_landing_time',
  'called_passenger',
];

/**
 * WHEELS DOWN, and nothing else — '' until the airline says the plane has
 * landed. `flight_status.expected_arrival` is deliberately NOT used: it
 * falls back to the revised estimate and then to the timetable, so a
 * countdown built on it would run out while the plane was still in the air.
 */
export const noShowLandedAt = (trip) => flightStatus(trip)?.actual_arrival || '';

/**
 * Which clock, if any, governs this trip's no-show:
 *   'actual_landing' — the plane is down; the 60 minutes are running
 *   'unknown'        — an airport pickup whose landing we could not read
 *   'not_airport'    — no flight is involved; the rule never applies
 */
export const noShowBasis = (trip) => {
  if (!isAirportPickupTrip(trip)) return 'not_airport';
  return noShowLandedAt(trip) ? 'actual_landing' : 'unknown';
};

/** The first moment a report is permitted (ISO), or '' when no clock runs. */
export const noShowAllowedAt = (trip) => {
  const landed = instant(noShowLandedAt(trip));
  return Number.isFinite(landed) ? new Date(landed + NO_SHOW_WAIT_MS).toISOString() : '';
};

/** Milliseconds still to wait; 0 once it is up, and 0 when nothing is running. */
export const noShowWaitRemaining = (trip, now = Date.now()) => {
  const allowed = instant(noShowAllowedAt(trip));
  if (!Number.isFinite(allowed)) return 0;
  return Math.max(0, allowed - new Date(now).valueOf());
};

/** Whole minutes waited since the landing; 0 before it, and 0 without one. */
export const noShowWaitedMinutes = (trip, now = Date.now()) => {
  const landed = instant(noShowLandedAt(trip));
  if (!Number.isFinite(landed)) return 0;
  return Math.max(0, Math.floor((new Date(now).valueOf() - landed) / 60000));
};

/** Has the 60 minutes run out? Never true without a landing to count from. */
export const noShowWaitElapsed = (trip, now = Date.now()) => (
  noShowBasis(trip) === 'actual_landing' && noShowWaitRemaining(trip, now) === 0
);

/**
 * Every recorded attempt to reach the customer — `[{ at, method }]`, oldest
 * first. WHATSAPP COUNTS: the instruction names the call button, but what it
 * asks for is a recorded attempt to reach the passenger, and at an airport a
 * message (prefilled with the meeting point, the car and the plate) is often
 * the better move. Do not narrow this back to calls.
 */
export const noShowContactAttempts = (trip) => trip?.passenger_contact_attempts || [];

/** The last one, or null — what the sheet shows instead of trusting its tick. */
export const lastContactAttempt = (trip) => noShowContactAttempts(trip).slice(-1)[0] || null;

/** Has the app itself recorded an attempt to reach this customer, either way? */
export const hasContactAttempt = (trip) => noShowContactAttempts(trip).length > 0;

/** The latest answer to "Névtáblával várakozol?", or null if never asked. */
export const nameSignAnswer = (trip) => trip?.name_sign || null;

/**
 * The four labelled photo slots. Each is optional — "lehetőség szerint" —
 * and none of them gates the report. The category is stored WITH the photo:
 * an unlabelled pile of JPEGs is nearly useless to whoever opens it a year
 * later, when a partner disputes the claim.
 */
export const NO_SHOW_PHOTO_CATEGORIES = ['meeting_point', 'conversation', 'name_sign', 'other'];

/**
 * Is there a number to ring at all? The API withholds the customer's phone
 * outside 24 hours either side of pickup, and a driver cannot be asked to
 * call a number they were never given.
 */
export const canCallCustomer = (trip) => Boolean(trip?.customer_phone);

/** The grounds of a no-show already filed, or null. */
export const noShowEvidence = (trip) => trip?.no_show_evidence || null;

/**
 * WHAT STILL STANDS BETWEEN THE DRIVER AND THE BUTTON — the whole gate in
 * one place, as a list of reason codes, so the sheet renders it and the
 * tests read it without going through the DOM.
 *
 * `[]` means the report may go. Anything in it keeps the button disabled.
 */
export const noShowBlockers = (trip, {
  checkpoints = [], note = '', officeAgreed = false, now = Date.now(),
} = {}) => {
  const blockers = [];
  const ticked = new Set(checkpoints);

  NO_SHOW_CHECKPOINTS.forEach((checkpoint) => {
    // The call step is not the driver's to claim: it is ticked by the app
    // having recorded one (see `NO_SHOW_COPY.callStep`). Where there is no
    // number at all it cannot be asked for, and the office's agreement —
    // which such a trip needs anyway — carries it instead.
    if (checkpoint === 'called_passenger') {
      if (canCallCustomer(trip) && !hasContactAttempt(trip)) blockers.push('call');
      return;
    }
    if (!ticked.has(checkpoint)) blockers.push('checkpoints');
  });

  if (!String(note || '').trim()) blockers.push('note');

  if (noShowBasis(trip) === 'actual_landing') {
    if (!noShowWaitElapsed(trip, now)) blockers.push('wait');
  } else if (!officeAgreed) {
    blockers.push('office');
  }

  return [...new Set(blockers)];
};

/**
 * What the driver actually SENDS — built here so the sheet has no shaping
 * logic of its own and the tests can read the body without a render.
 * `called_passenger` reflects the app's record, never the checkbox.
 */
export const noShowEvidenceBody = (trip, {
  checkpoints = [], note = '', officeAgreed = false, photos = [], nameSign = null,
} = {}) => {
  const confirmed = NO_SHOW_CHECKPOINTS.filter((checkpoint) => (
    checkpoint === 'called_passenger'
      ? hasContactAttempt(trip) || !canCallCustomer(trip)
      : checkpoints.includes(checkpoint)
  ));

  return {
    checkpoints: confirmed,
    note: String(note || '').trim(),
    office_agreed: officeAgreed === true,
    ...(photos.length ? { photos } : {}),
    // Sent whenever the driver answered, INCLUDING "no sign at all" — that
    // is an answer about how visible they were, not a blank.
    ...(nameSign?.kind ? { name_sign: nameSign } : {}),
  };
};

/** NoShowSheet, and the name-sign prompt that feeds its photo. */
export const NO_SHOW_COPY = {
  title: 'Nem jelent meg az utas?',
  // The instruction's own opening line, so the sheet reads as the procedure
  // it is rather than as a warning.
  lead: 'A no-show jelentés csak az alábbi lépések elvégzése után küldhető el.',
  airportRule: 'Repülőtéri fuvar: 60 percet a járat tényleges landolása után kell várni.',

  /* --- the clock --- */
  waitLabel: 'A 60 percből hátra van',
  // Which instant it is counted from. Named on screen because it is the one
  // thing a driver could otherwise get wrong by an hour.
  countedFrom: (landedAt) => `A járat ${landedAt}-kor szállt le — innen számoljuk a 60 percet, `
    + 'nem a tervezett érkezéstől és nem a felvételi időtől.',
  waitOver: (landedAt, minutes) => `A járat ${landedAt}-kor szállt le, azóta ${minutes} perc telt el. `
    + 'A 60 perc letelt.',
  // The existing FlightCard sentence, so the two screens say the same thing
  // about the same failure.
  landingUnknown: `${FLIGHT_COPY.unknown} Így nem tudjuk kiszámolni a 60 percet, `
    + 'ezért csak az irodával egyeztetve jelents no-show-t.',
  notAirport: 'Ez nem repülőtéri fuvar. Előzetes egyeztetés nélkül ne jelents no-show-t.',

  /* --- the four steps --- */
  stepsTitle: 'Lépések',
  steps: {
    stayed_at_meeting_point: {
      label: 'A kijelölt találkozási ponton vártam',
      hint: 'Ne menj el a parkolóba vagy másik terminálhoz anélkül, hogy azt a '
        + 'kapcsolattartóval egyeztetnéd. A GPS-nek igazolnia kell, hogy a megfelelő helyen várakoztál.',
    },
    checked_landing_time: {
      label: 'Ellenőriztem a tényleges landolási időt',
      hint: 'A 60 percet a járat tényleges landolásától kell számítani.',
    },
    called_passenger: {
      label: 'Kerestem az utast az alkalmazásból',
      hint: 'Hívd fel az utast, vagy írj neki WhatsAppon az alkalmazásból, '
        + 'hogy a próbálkozás rögzítve legyen.',
    },
  },

  /* --- what the app knows about reaching the passenger --- */
  callStep: 'Hívd fel az utast',
  /** Which route was actually used — two different facts in a vita. */
  contactRecorded: (method, at) => (method === 'whatsapp'
    ? `WhatsApp üzenet rögzítve: ${at}`
    : `Hívási kísérlet rögzítve: ${at}`),
  callMissing: 'Még nem kerested az utast ebből az alkalmazásból. Nyomd meg a Hívás vagy a '
    + 'WhatsApp gombot az utas adatainál — enélkül nem küldhető el a jelentés.',
  callNoNumber: 'Az utas telefonszáma most nem elérhető, ezért nem tudunk próbálkozást rögzíteni. '
    + 'Egyeztess az irodával.',

  /* --- the GPS --- */
  // Three states, not two: while the phone is still thinking, neither
  // claiming corroboration nor warning about its absence would be true.
  positionLocating: 'A telefon helyzetének lekérdezése…',
  positionOk: 'A telefon helyzetét csatoljuk a jelentéshez.',
  // The honest version of the missing 7%. It does not block the report: the
  // office is told that this one has no corroboration, which is the point.
  positionMissing: 'A telefon most nem ad helyadatot, ezért a jelentés nem tudja igazolni, '
    + 'hol várakoztál. Az iroda ezt látni fogja.',

  /* --- the note and the photos --- */
  noteLabel: 'Rövid megjegyzés',
  notePlaceholder: 'Pl. 2A kijárat, névtáblával vártam, nem jött ki senki.',
  noteMissing: 'Írj egy rövid megjegyzést arról, mi történt.',
  photoLabel: 'Fénykép',
  photoOptional: ' · nem kötelező',
  /** One labelled slot each, so a photo says what it is meant to prove. */
  photoSlots: {
    meeting_point: {
      label: 'Fotó a találkozási pontról',
      // "lehetőleg", never a requirement: we cannot verify a clock in a
      // photo anyway. What actually timestamps it is our own upload and the
      // GPS on the event — the driver's picture corroborates, it does not
      // carry the proof on its own.
      hint: 'Lehetőleg úgy, hogy látszódjon egy óra vagy időbélyeg.',
      alt: 'A találkozási pont fotója',
    },
    conversation: {
      label: 'Fotó a beszélgetésedről / hívásokról',
      // We already log that the app's Hívás / WhatsApp button was tapped;
      // this shows what happened after it. Neither replaces the other.
      hint: 'Képernyőkép a chatről vagy a hívásnaplóról.',
      alt: 'A beszélgetés fotója',
    },
    name_sign: {
      label: 'Fotó a névtábláról',
      hint: 'Ha saját névtáblával vártál.',
      alt: 'A névtábla fotója',
    },
    other: {
      label: 'Egyéb bizonyíték fotó',
      // The parking ticket is singled out because it is the strongest thing
      // a driver can produce: a third party's own record of the time and
      // the place, proving they were there and paid to be.
      hint: 'Pl. parkolási vagy behajtási engedély, számla. A parkolójegy a legerősebb '
        + 'bizonyíték: egy külső fél igazolja rajta az időt és a helyet.',
      alt: 'Egyéb bizonyíték fotója',
    },
  },
  processingPhoto: 'Fotó feldolgozása…',
  photoReady: 'Kész',
  retake: 'Új fotó',
  photoUnreadable: 'Ezt a fotót nem sikerült beolvasni. Készíts egy újat.',
  /** The name-sign slot filled in from the photo taken while waiting. */
  signPhotoAttached: 'A várakozás közben készült kép — csatoljuk.',
  /** …and why that slot is empty and closed when the sign was ours. */
  appSignNoPhoto: 'Az alkalmazás névtáblájával vártál, ezt rögzítettük — fotó nem kell.',

  /* --- the office --- */
  officeLabel: 'Egyeztettem az irodával, és jóváhagyták a no-show-t',
  // Asking permission, which is what the office's step 4 requires where no
  // landing clock can decide — so the message says so rather than
  // announcing a decision already taken.
  officeMessage: (trip, driverName) => officeMessage({
    trip, driverName, situation: 'no_show_permission',
  }),

  /* --- the buttons --- */
  cancel: 'Mégse',
  submit: 'Igen, nem jelent meg',
  sending: 'Küldés…',
  // What the button is waiting for, in the order the sheet asks for it.
  blocked: {
    wait: 'A 60 perc még nem telt le.',
    checkpoints: 'Jelöld be a lépéseket.',
    call: 'Hívd fel az utast, vagy írj neki WhatsAppon az alkalmazásból.',
    note: 'Írj egy rövid megjegyzést.',
    office: 'Előbb egyeztess az irodával.',
  },
};

/**
 * "Nem jelent meg" tapped while the free waiting is STILL RUNNING.
 *
 * A warning, not a block. There are real reasons to report early — the
 * passenger has texted to say they are not coming — and the hard gate is
 * the 60-minute airport rule, which the server enforces. This is the softer
 * "are you sure", and it names the NUMBER rather than saying "too early",
 * because a driver deciding whether to wait needs the minutes.
 *
 * The two phases read differently on purpose: a clock that has not started
 * (the driver turned up before the pickup time) is not the same situation
 * as one with twelve minutes left on it.
 */
export const NO_SHOW_EARLY_COPY = {
  running: (left) => `Még ${left} ingyenes várakozási idő van hátra. `
    + 'Biztos, hogy nem jelent meg az utas?',
  early: (startsAt) => `Az ingyenes várakozás még el sem kezdődött — ${startsAt}-kor indul. `
    + 'Biztos, hogy nem jelent meg az utas?',
  confirm: 'Igen, jelentem',
};

/**
 * Should tapping "Nem jelent meg" warn first? Only while the allowance is
 * still running or has not started; once it has expired there is nothing
 * left to warn about.
 */
export const noShowIsEarly = (trip, now = Date.now()) => {
  const phase = freeWaitingPhase(trip, now);
  return phase === 'early' || phase === 'running';
};

/** The name-sign prompt shown while waiting at an airport pickup. */
export const NAME_SIGN_ASK_COPY = {
  question: 'Névtáblával várakozol?',
  yes: 'Igen',
  no: 'Nem',
  /** Igen → whose sign. The answer decides whether a photo is asked for. */
  kindQuestion: 'Melyik névtáblával?',
  app: 'Appos névtábla',
  own: 'Saját',
  /**
   * The app's own sign needs no photograph, and the reason is worth saying
   * out loud: we drew it, so we know exactly what it showed. Asking a
   * driver to photograph their own phone would be absurd.
   */
  appChosen: (name) => (name
    ? `Az alkalmazás névtáblája: „${name}". Ezt rögzítjük — fotózni nem kell.`
    : 'Az alkalmazás névtáblája. Ezt rögzítjük — fotózni nem kell.'),
  upload: 'Kép beküldés névtábláról',
  // Why it is worth the tap: this photo is what proves the driver was
  // visibly there, and it is the same evidence a no-show would need later.
  why: 'A kép igazolja, hogy láthatóan ott vártál. Ha az utas nem jelenik meg, '
    + 'ezt csatoljuk a jelentéshez.',
  saved: 'Kép mentve — a no-show jelentéshez csatoljuk.',
  processing: 'Fotó feldolgozása…',
  unreadable: 'Ezt a fotót nem sikerült beolvasni. Készíts egy újat.',
  alt: 'A névtábla fotója',
  retake: 'Új fotó',
};

/* --- writing to the PASSENGER ------------------------------------------
 * The one place in the driver portal whose words are not for the driver.
 *
 * The CustomerCard's WhatsApp button used to open an empty chat: a driver
 * standing in arrivals, a stranger's phone, no shared language and nothing
 * typed. So it now opens with a sentence that says who is writing, where
 * they are, and what the car looks like — the things that actually end a
 * "where are you?" standoff.
 *
 * IT IS IN ENGLISH, DELIBERATELY, AND IT MUST NOT BE "FIXED" INTO A GUESS.
 * The booking interface has a `language` field ('en' | 'hu' | 'ru') and it
 * is NULL on all 13,856 bookings in production — every one, including all
 * of the last 90 days. Nothing has ever written it. Nor is the language
 * inferable: over the last 180 days 53% of passenger numbers carry a
 * country code outside the common set, Israel is the third largest group,
 * and a country code is a weak proxy anyway (a German-registered phone is
 * not a German speaker; +7 covers Russia and Kazakhstan). A wrong guess is
 * worse than none — a Russian message to a British passenger reads as a
 * scam, from a number they do not know, at an airport. English is the
 * lingua franca of an international transfer business, and it is a choice,
 * not a default.
 *
 * NOTHING COMMERCIAL GOES IN IT. No price, no partner, no operator, no
 * internal note — this text goes to a customer. It does not name Pickup
 * Hungary either: a white-label partner's guest must not be greeted by us
 * (`ph_branding_allowed` exists for exactly that reason), and "your driver"
 * is true on every booking.
 *
 * The message is chosen by WHERE THE TRIP ACTUALLY IS, never from a menu —
 * a driver who has not set off cannot send "I am waiting for you".
 */

/**
 * Which one fits right now, or '' when none does.
 *
 * '' before the driver has tapped "Úton vagyok" (nothing has happened yet,
 * and the app will not put a claim in their mouth) and once the passenger
 * is aboard (there is nobody to write to any more).
 */
export const passengerMessageState = (trip, now = Date.now()) => {
  const step = progressIndex(trip);

  if (step === 2) {
    // The allowance has run out and still nobody: this is the message that
    // belongs with step 3 of the no-show instruction, not a separate idea.
    return freeWaitingPhase(trip, now) === 'expired' ? 'cannot_find' : 'waiting';
  }

  if (step === 1) {
    const pickup = instant(trip?.date_datetime);
    const late = Number.isFinite(pickup) && new Date(now).valueOf() > pickup;
    return late ? 'running_late' : 'on_the_way';
  }

  return '';
};

/** "My car is a Mercedes V-Class, plate ABC-123." — or as much as is known. */
const carSentence = (trip) => {
  const type = trip?.vehicle?.type || '';
  const plate = trip?.vehicle?.plate_number || '';

  if (type && plate) return `My car is a ${type}, plate ${plate}.`;
  if (plate) return `My car's plate is ${plate}.`;
  if (type) return `My car is a ${type}.`;
  return '';
};

/**
 * The text the WhatsApp chat opens with, or '' when there is nothing
 * honest to say. `nameSign` is what the driver is holding up, when they
 * answered "Névtáblával várakozol?" with Igen — the single most useful
 * thing a passenger scanning an arrivals hall can be told.
 */
export const passengerMessage = ({
  trip = null, driverName = '', nameSign = '', now = Date.now(),
} = {}) => {
  const state = passengerMessageState(trip, now);
  if (!state) return '';

  const hello = [
    trip?.customer_name ? `Hello ${firstName(trip.customer_name)},` : 'Hello,',
    driverName ? `this is ${firstName(driverName)}, your driver.` : 'this is your driver.',
  ].join(' ');

  const where = trip?.from?.address || '';
  const car = carSentence(trip);
  const sign = nameSign ? `I am holding a name sign that says "${nameSign}".` : '';

  const body = {
    on_the_way: where ? `I am on my way to ${where}.` : 'I am on my way to you.',
    running_late: where
      ? `I am running late to ${where} — I will be there as soon as I can.`
      : 'I am running a little late — I will be there as soon as I can.',
    waiting: where ? `I am waiting for you at ${where}.` : 'I am waiting for you at the pickup point.',
    // The step before a no-show. It says what the driver has already done,
    // so the passenger understands this is the last try and not small talk.
    cannot_find: where
      ? `I am waiting at ${where} and I cannot find you. I tried to call. Where are you now?`
      : 'I cannot find you at the pickup point. I tried to call. Where are you now?',
  }[state];

  // The car and the sign only help someone who is looking for it, so they
  // ride with the two waiting messages and not with "I am on my way".
  const extras = state === 'waiting' ? [car, sign] : [];
  if (state === 'cannot_find') extras.push(sign);

  return [hello, body, ...extras].filter(Boolean).join(' ');
};

/**
 * The terms in force for this trip, as three lines with the live one marked.
 *
 * WHICH LINE IS LIVE IS NOT WORKED OUT HERE. `airport` and both allowances
 * come from the window the API computed, off the same resolved policy that
 * produced the clock — so the marked line and the countdown above it cannot
 * disagree. Re-reading the pickup address on the phone is exactly the way
 * they would.
 *
 * Null for a trip whose window predates the wider projection: better no
 * panel than "undefined perc".
 */
export const freeWaitingTerms = (trip, copy = FREE_WAITING_COPY) => {
  const window = freeWaiting(trip);
  if (!window) return null;

  // `Number(null)` and `Number('')` are both 0, so a missing allowance has
  // to be ruled out BEFORE the cast — otherwise a window from before the
  // projection carried both numbers would render "0 perc várakozás" as
  // though the channel gave none.
  const minutes = (value) => (value === null || value === undefined || value === ''
    ? null
    : Number(value));
  const airportMinutes = minutes(window.airport_minutes);
  const standardMinutes = minutes(window.standard_minutes);
  if (!Number.isFinite(airportMinutes) || !Number.isFinite(standardMinutes)) return null;

  const house = window.policy_source !== 'partner';

  return {
    title: house ? copy.termsTitleHouse : copy.termsTitle,
    // Said once, quietly: the driver cannot fix it, and the office is
    // warned where it can be (the booking form).
    note: house ? copy.termsHouseNote : '',
    rows: [
      {
        key: 'standard',
        text: copy.termsStandard(standardMinutes),
        active: window.airport === false,
      },
      {
        key: 'airport',
        text: copy.termsAirport(airportMinutes),
        active: window.airport === true,
      },
      {
        key: 'delay',
        text: copy.termsDelay(window.max_flight_delay_minutes, window.minutes),
        active: false,
      },
    ],
  };
};
